import { useId, useMemo, useState, type ReactNode } from 'react';
import {
  Alert,
  ButtonIcon,
  Switch,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';
import type { RemixiconComponentType } from '@remixicon/react';
import { useTranslationRef } from '@backstage/frontend-plugin-api';
import {
  applyProfile,
  configurationBounds,
  hiddenFieldIds,
  isDatabaseMode,
  lockedFieldIds,
  peakReservation,
  profileOf,
  sizedReservation,
  unitText,
  type DatabaseMode,
  type DescriptorComponent,
  type DescriptorField,
  type FieldValue,
  type FieldValues,
  type ResourceKey,
} from '@internal/plugin-waadoo-config-common';
import type { ConfigurationTabProps } from '../../appModules/appTypeModule';
import { waadooIcons } from '../../ui/icons/waadooIcons';
import {
  ProfileCards,
  SectionCard,
  WorkbenchAside,
  WorkbenchMain,
  type ProfileCardOption,
} from '../../waadooForm';
import { CUSTOM_INFO } from '../../waadooForm/customProfile';
import { formatCpu, formatMemory } from '../../waadooForm/formats';
import {
  CUSTOM_PROFILE,
  descriptorOf,
  environmentValues,
  type ConfigurationResponse,
} from '../configuration';
import { projectEnvironmentsTranslationRef } from '../translation';
import { ConfigurationRow } from './ConfigurationRow';
import {
  ConfigurationSidePanel,
  useConfigurationApply,
  useConfigurationReference,
} from './ConfigurationSidePanel';
import { ConfigurationWorkbench } from './ConfigurationWorkbench';
import { DatabaseSection } from './DatabaseSection';
import { DATABASE_MODE_KEYS } from './databaseFields';
import {
  componentFigures,
  genericLayout,
  hpaVisible,
  planGenericDraft,
  problemFields,
  rangedFor,
  sectionOpen,
  type ComponentPart,
  type GenericProblem,
  type GenericSection,
  type RoleFields,
} from './genericConfiguration';
import { useConfigurationRows } from './useConfigurationRows';

const LockedIcon = waadooIcons.locked;

/** The locks said in the core's words. */
const LOCK_KEYS = {
  'read-only': 'configuration.lockReadOnly',
  production: 'configuration.lockProduction',
  ephemeral: 'configuration.lockEphemeralGeneric',
} as const;

const NUMBER = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 });

/** A constant resource of a component, as its unit is typed (`500 m`). */
const RESOURCE_UNITS: Readonly<Record<ResourceKey, string>> = {
  cpuRequest: 'm',
  cpuLimit: 'm',
  memoryRequest: 'Mio',
  memoryLimit: 'Mio',
};

/** The instances of each PostgreSQL mode, as the shared section says them. */
const MODE_ITEMS = [
  { id: 'single', key: 'configuration.instances1' },
  { id: 'ha2', key: 'configuration.instances2' },
  { id: 'ha3zones', key: 'configuration.instances3' },
] as const;

/**
 * The core's « Configuration » tab, for every type whose app-type module
 * contributes none (`useConfigurationTab`; genericity plan §2, §2 bis, step
 * 5, 2026-10-24) — Odoo keeps its own (`plugins/odoo`). The frame
 * (`ConfigurationWorkbench`), the rows (`useConfigurationRows`), the
 * PostgreSQL section (`DatabaseSection`) and the quota gauges are the ones
 * the Odoo tab is built on; the sections come from the type's descriptor
 * (`genericLayout`): its capacity profiles, each application component
 * (the main one unfolded, the others folded with a summary, a `group` as
 * one compact table), the volumes, PostgreSQL, MySQL/MariaDB, Valkey, then
 * « Autres réglages ». The labels are the descriptor's (French); the
 * structure's words are the core's translations.
 */
export function GenericConfigurationTab(props: ConfigurationTabProps) {
  const { project, branch, templateRef, reloadSignal, onLaunched } = props;
  return (
    <ConfigurationWorkbench<FieldValues>
      project={project}
      branch={branch}
      reloadSignal={reloadSignal}
      renderEditor={({ answer, current, permanentNames }) => (
        <GenericConfigurationEditor
          project={project}
          branch={branch}
          templateRef={templateRef}
          answer={answer}
          current={current}
          permanentNames={permanentNames}
          reloadSignal={reloadSignal}
          onLaunched={onLaunched}
        />
      )}
    />
  );
}

/** The lock of a cell of a group's table: as `ConfigurationRow` draws it. */
function LockMark(props: { label: string }) {
  return (
    <TooltipTrigger>
      <ButtonIcon
        variant="tertiary"
        size="small"
        aria-label={props.label}
        className="waadoo-config__lock"
        icon={<LockedIcon aria-hidden focusable="false" />}
      />
      <Tooltip>{props.label}</Tooltip>
    </TooltipTrigger>
  );
}

function GenericConfigurationEditor(props: {
  project: string;
  branch: string;
  templateRef: string | undefined;
  answer: ConfigurationResponse<FieldValues>;
  current: FieldValues;
  permanentNames: string[];
  reloadSignal?: number;
  onLaunched: (taskId: string) => void;
}) {
  const {
    project,
    branch,
    templateRef,
    answer,
    current,
    permanentNames,
    onLaunched,
  } = props;
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  const idPrefix = useId();
  const [draft, setDraft] = useState<FieldValues>(current);
  // A folded section's state, by its key, once the reader toggled it.
  const [toggled, setToggled] = useState<Readonly<Record<string, boolean>>>({});

  const editable = answer.editable;
  const grant = answer.access === 'admin' ? 'admin' : 'editor';
  // Compiled once by `fetchConfiguration` (`descriptorOf` caches it).
  const descriptor = useMemo(
    () => descriptorOf(answer.descriptor),
    [answer.descriptor],
  );
  const bounds = useMemo(
    () => configurationBounds(descriptor, grant),
    [descriptor, grant],
  );
  // The fields meaningless for this environment (their `when`), over its
  // STORED values — its non-configurable fields (Wagtail's `mediaS3`) and
  // its configuration: not shown, never set by a profile, never sent.
  const hidden = useMemo(
    () =>
      hiddenFieldIds(
        descriptor,
        environmentValues({ inputs: answer.inputs, configuration: current }),
      ),
    [descriptor, answer.inputs, current],
  );
  const layout = useMemo(
    () => genericLayout(descriptor, grant, hidden),
    [descriptor, grant, hidden],
  );
  const locked = useMemo(
    () => lockedFieldIds(descriptor, current, grant),
    [descriptor, current, grant],
  );
  const frozen = useMemo(
    () => new Set([...locked, ...hidden]),
    [locked, hidden],
  );
  const plan = useMemo(
    () => planGenericDraft({ descriptor, current, draft, grant }),
    [descriptor, current, draft, grant],
  );
  const applying = useConfigurationApply({
    project,
    branch,
    templateRef,
    onLaunched,
  });
  const { pending } = applying;
  const sleeping = answer.kind === 'permanent' && !answer.active;

  const compare = useConfigurationReference<FieldValues>({
    project,
    branch,
    permanentNames,
    reloadSignal: props.reloadSignal,
  });
  const { reference, referenceEnvironment, referenceLabel } = compare;

  /** A configurable field of the descriptor: every id the layout holds. */
  const fieldOf = (id: string): DescriptorField => {
    const field = descriptor.fieldById.get(id);
    if (!field) {
      throw new Error(`Champ inconnu du type ${descriptor.name} : ${id}`);
    }
    return field;
  };
  const isDisabled = (id: string) => !editable || pending || locked.has(id);
  const set = (id: string, value: FieldValue) =>
    setDraft(previous => ({ ...previous, [id]: value }));

  const valueText = (id: string, value: FieldValue): string => {
    if (typeof value === 'boolean') {
      return value ? t('configuration.yes') : t('configuration.no');
    }
    if (isDatabaseMode(value) && fieldOf(id).type === 'databaseMode') {
      return t(DATABASE_MODE_KEYS[value]);
    }
    if (typeof value === 'string') return value;
    const unit = unitText(fieldOf(id));
    const text = NUMBER.format(value);
    return unit ? `${text}\u00a0${unit}` : text;
  };
  const problemText = (problem: GenericProblem): string => {
    if (problem.key === 'text') {
      // A rule's refusal: the descriptor's French.
      return problem.text;
    }
    const field = fieldOf(problem.field).label;
    switch (problem.key) {
      case 'locked':
        return t('configuration.problemLocked', { field });
      case 'bounds': {
        const text = t('configuration.problemBounds', {
          field,
          min: String(problem.values.min),
          max: String(problem.values.max),
          step: String(problem.values.step),
        });
        return grant === 'editor'
          ? `${text} ${t('configuration.problemBeyondEditors')}`
          : text;
      }
      case 'values':
        return t('configuration.problemValues', {
          field,
          allowed: String(problem.values.allowed),
        });
      default:
        return t('configuration.problemShrink', {
          field,
          current: String(problem.values.current),
        });
    }
  };
  const invalidFields = new Set(plan.problems.flatMap(problemFields));
  const problemsOf = (...fields: string[]) =>
    plan.problems
      .filter(
        problem =>
          problem.field !== undefined && fields.includes(problem.field),
      )
      .map(problemText);
  const rows = useConfigurationRows<FieldValues>({
    idPrefix,
    ranges: bounds.ranges,
    draft,
    current,
    reference,
    referenceEnvironment,
    referenceLabel,
    locked,
    label: id => fieldOf(id).label,
    unit: id => unitText(fieldOf(id)),
    valueText,
    isDisabled,
    invalid: id => invalidFields.has(id),
    problems: problemsOf,
    set,
  });
  const { ids, was, hintText, referenceText, fieldsText, number, choice } =
    rows;
  const adminsOnly = rows.lockedLabel;

  // The fields shown by the rows built below: a problem said under none of
  // them (a rule without a field, or on a field not shown — the pods while
  // the autoscaling is on) is said in the panel.
  const shown = new Set<string>();
  const show = (...fields: string[]) => fields.forEach(id => shown.add(id));

  const changed = (id: string) =>
    draft[id] !== current[id] || plan.desired[id] !== current[id];
  const differs = (id: string) =>
    reference !== undefined && reference[id] !== draft[id];

  /** The control of a field, by its type and the caller's bound. */
  const control = (id: string, labelledBy: string): ReactNode => {
    const field = fieldOf(id);
    if (field.type === 'boolean' || field.type === 'databaseSync') {
      return (
        <Switch
          aria-labelledby={labelledBy}
          // As the plan writes it: a rule may impose it (`adjusted`).
          isSelected={plan.desired[id] === true}
          isDisabled={isDisabled(id)}
          onChange={value => set(id, value)}
        />
      );
    }
    if (field.type === 'databaseMode') {
      return choice<DatabaseMode>({
        label: field.label,
        selected: modeOf(draft, id),
        items: MODE_ITEMS.map(item => ({ id: item.id, label: t(item.key) })),
        disabled: isDisabled(id),
        onSelect: mode => set(id, mode),
      });
    }
    if (bounds.ranges[id]) {
      const now = current[id];
      return number(
        id,
        labelledBy,
        field.growOnly && typeof now === 'number' ? now : undefined,
      );
    }
    // A list of values: a value beyond it (an administrator's) is offered
    // too, so that the control shows what the environment holds.
    const values = [...(bounds.values[id] ?? [])];
    if (!values.includes(draft[id])) values.push(draft[id]);
    return choice<string>({
      label: field.label,
      selected: String(draft[id]),
      items: values.map(value => ({
        id: String(value),
        label: valueText(id, value),
      })),
      disabled: isDisabled(id),
      onSelect: key => {
        const value = values.find(entry => String(entry) === key);
        if (value !== undefined) set(id, value);
      },
    });
  };
  /** Said under a switch a rule imposes, whatever the reader asks. */
  const adjusted = (id: string) => {
    const type = fieldOf(id).type;
    return (type === 'boolean' || type === 'databaseSync') &&
      plan.desired[id] !== draft[id]
      ? t('configuration.adjusted')
      : undefined;
  };
  /** One field, one row: its descriptor's label. */
  const fieldRow = (id: string, hint?: string): ReactNode => {
    show(id);
    return (
      <ConfigurationRow
        key={id}
        label={fieldOf(id).label}
        labelId={ids(id)}
        locked={locked.has(id)}
        lockedLabel={adminsOnly}
        hint={hintText(was(id), hint, adjusted(id))}
        reference={referenceText(fieldsText(id))}
        problems={problemsOf(id)}
      >
        {control(id, ids(id))}
      </ConfigurationRow>
    );
  };
  const ranged = (id: string) => rangedFor(descriptor, grant, id);
  /** A reserved / ceiling pair in one row, else each of them in its own. */
  const pair = (
    key: string,
    label: string,
    request: string | undefined,
    limit: string | undefined,
    hint: string,
  ): ReactNode => {
    if (request && limit && ranged(request) && ranged(limit)) {
      show(request, limit);
      return rows.pairRow(key, label, request, limit, hint);
    }
    return [request, limit].flatMap(id =>
      id === undefined ? [] : [fieldRow(id, hint)],
    );
  };
  const resourceRows = (
    component: DescriptorComponent,
    roles: RoleFields,
    hint: string,
  ) => [
    pair(
      `${component.id}-cpu`,
      t('configuration.componentCpu', { component: component.label }),
      roles.cpuRequest,
      roles.cpuLimit,
      hint,
    ),
    pair(
      `${component.id}-memory`,
      t('configuration.componentMemory', { component: component.label }),
      roles.memoryRequest,
      roles.memoryLimit,
      hint,
    ),
  ];
  /** The autoscaling's minimum → maximum, one row when both are inputs. */
  const hpaRange = (
    component: DescriptorComponent,
    min: string | undefined,
    max: string | undefined,
  ): ReactNode => {
    if (min && max && ranged(min) && ranged(max)) {
      show(min, max);
      const labelId = ids(`${component.id}-pods`);
      return (
        <ConfigurationRow
          key={`${component.id}-pods`}
          label={t('configuration.componentPods', {
            component: component.label,
          })}
          labelId={labelId}
          locked={locked.has(min) || locked.has(max)}
          lockedLabel={adminsOnly}
          hint={hintText(was(min, max))}
          reference={referenceText(fieldsText(min, max))}
          problems={problemsOf(min, max)}
        >
          {number(min, labelId)}
          <span aria-hidden="true">→</span>
          {number(max, labelId)}
        </ConfigurationRow>
      );
    }
    return [min, max].flatMap(id => (id === undefined ? [] : [fieldRow(id)]));
  };
  /** The rows of an application component, its roles in order. */
  const appRows = (part: ComponentPart): ReactNode[] => {
    const { component, roles } = part;
    const hpa = hpaVisible(roles, plan.desired);
    return [
      roles.enabled && fieldRow(roles.enabled),
      ...resourceRows(component, roles, t('configuration.perPod')),
      // The pods without autoscaling: the HPA's bounds replace them.
      roles.replicas &&
        !(roles.hpaEnabled && plan.desired[roles.hpaEnabled] === true) &&
        fieldRow(roles.replicas),
      roles.hpaEnabled && fieldRow(roles.hpaEnabled),
      hpa && hpaRange(component, roles.hpaMin, roles.hpaMax),
      hpa && roles.hpaTargetCpu && fieldRow(roles.hpaTargetCpu),
      hpa && roles.hpaScaleDownWindow && fieldRow(roles.hpaScaleDownWindow),
    ];
  };

  const summaryOf = (part: ComponentPart): string => {
    const figures = componentFigures(part, plan.desired);
    if (figures.disabled) return t('configuration.componentDisabled');
    const { pods } = figures;
    let podsText: string | undefined;
    if (typeof pods === 'number') {
      podsText = t('configuration.podCount', { count: pods });
    } else if (pods) {
      podsText = t('configuration.podRange', {
        min: String(pods.min),
        max: String(pods.max),
      });
    }
    return [
      podsText,
      figures.cpu !== undefined ? formatCpu(figures.cpu) : undefined,
      figures.memory !== undefined ? formatMemory(figures.memory) : undefined,
    ]
      .filter(Boolean)
      .join(' · ');
  };

  /** A section folded unless it has a problem, a change or a difference. */
  const folded = (input: {
    key: string;
    icon: RemixiconComponentType;
    title: string;
    fields: string[];
    summary: string;
    body: ReactNode;
  }) => {
    const { key, fields } = input;
    const problems = fields.some(id => invalidFields.has(id));
    const open = sectionOpen({
      problems,
      toggled: toggled[key],
      byDefault: fields.some(id => changed(id) || differs(id)),
    });
    const count = fields.filter(differs).length;
    return (
      <SectionCard
        key={key}
        icon={input.icon}
        titleId={ids(key)}
        divider={open}
        title={
          <button
            type="button"
            className="waadoo-config__ha-toggle"
            aria-expanded={open}
            aria-disabled={problems ? 'true' : undefined}
            aria-controls={ids(`${key}-body`)}
            onClick={() => {
              if (!problems) {
                setToggled(previous => ({ ...previous, [key]: !open }));
              }
            }}
          >
            {input.title}
          </button>
        }
        aside={
          open ? undefined : (
            <Text
              as="span"
              variant="body-small"
              color="secondary"
              className="waadoo-config__ha-summary"
            >
              {[
                input.summary,
                count > 0 && referenceLabel
                  ? t('configuration.differences', {
                      count,
                      branch: referenceLabel,
                    })
                  : '',
              ]
                .filter(Boolean)
                .join(' — ')}
            </Text>
          )
        }
      >
        <div id={ids(`${key}-body`)} hidden={!open}>
          {input.body}
        </div>
      </SectionCard>
    );
  };

  /** A cell of a group's table: a field's control, or the constant. */
  const cell = (
    part: ComponentPart,
    role: 'replicas' | 'enabled' | ResourceKey,
    labelledBy: string,
  ): ReactNode => {
    const id = part.roles[role];
    if (id !== undefined) {
      show(id);
      return control(id, labelledBy);
    }
    if (role === 'replicas' || role === 'enabled') {
      const { pods } = componentFigures(part, plan.desired);
      return (
        <span>{typeof pods === 'number' ? NUMBER.format(pods) : '—'}</span>
      );
    }
    const ref = part.component.resources[role];
    return (
      <span>
        {typeof ref === 'number'
          ? `${NUMBER.format(ref)}\u00a0${RESOURCE_UNITS[role]}`
          : '—'}
      </span>
    );
  };
  const groupSection = (
    section: Extract<GenericSection, { kind: 'group' }>,
  ): ReactNode => {
    // By its first member: an id, where the group's name may hold spaces
    // (`aria-labelledby` is a list of ids separated by spaces).
    const key = `section-group-${section.members[0].component.id}`;
    const column = (name: string) => ids(`${key}-${name}`);
    const tableFields = section.fields.filter(
      id => !section.extra.includes(id),
    );
    const body = (
      <>
        <table className="waadoo-config__group-table">
          <thead>
            <tr>
              <th scope="col" id={column('component')}>
                {t('configuration.groupComponent')}
              </th>
              <th scope="col" id={column('pods')}>
                {t('configuration.groupPods')}
              </th>
              <th scope="col" id={column('cpu')}>
                {t('configuration.groupCpu')}
              </th>
              <th scope="col" id={column('memory')}>
                {t('configuration.groupMemory')}
              </th>
            </tr>
          </thead>
          <tbody>
            {section.members.map(part => {
              const rowId = ids(`${key}-${part.component.id}`);
              const by = (name: string) => `${rowId} ${column(name)}`;
              const [podsRole] = part.table;
              const lockedRow = Object.values(part.roles).some(
                id => id !== undefined && locked.has(id),
              );
              return (
                <tr key={part.component.id}>
                  <th scope="row" id={rowId}>
                    {part.component.label}
                    {lockedRow && <LockMark label={adminsOnly} />}
                  </th>
                  <td>
                    {cell(
                      part,
                      podsRole === 'enabled' ? 'enabled' : 'replicas',
                      by('pods'),
                    )}
                  </td>
                  <td>
                    {cell(part, 'cpuRequest', by('cpu'))}
                    <span aria-hidden="true"> / </span>
                    {cell(part, 'cpuLimit', by('cpu'))}
                  </td>
                  <td>
                    {cell(part, 'memoryRequest', by('memory'))}
                    <span aria-hidden="true"> / </span>
                    {cell(part, 'memoryLimit', by('memory'))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {problemsOf(...tableFields).map(text => (
          <Text
            key={text}
            as="p"
            variant="body-small"
            color="danger"
            role="alert"
          >
            {text}
          </Text>
        ))}
        {section.extra.map(id => fieldRow(id))}
      </>
    );
    return folded({
      key,
      icon: waadooIcons.pod,
      title: t('configuration.groupTitle', {
        group: section.group,
        members: String(section.members.length),
      }),
      fields: section.fields,
      summary: section.members.map(part => part.component.label).join(', '),
      body,
    });
  };

  const sectionOf = (section: GenericSection): ReactNode => {
    switch (section.kind) {
      case 'app': {
        const key = `section-app-${section.component.id}`;
        const body = appRows(section);
        return section.main ? (
          <SectionCard
            key={key}
            icon={waadooIcons.pod}
            title={section.component.label}
            titleId={ids(key)}
          >
            {body}
          </SectionCard>
        ) : (
          folded({
            key,
            icon: waadooIcons.pod,
            title: section.component.label,
            fields: section.fields,
            summary: summaryOf(section),
            body,
          })
        );
      }
      case 'group':
        return groupSection(section);
      case 'volumes':
        return (
          <SectionCard
            key="section-volumes"
            icon={waadooIcons.volume}
            title={t('configuration.volumesSection')}
            titleId={ids('section-volumes')}
          >
            {section.volumes.map(volume =>
              fieldRow(
                volume.field,
                [
                  fieldOf(volume.field).growOnly
                    ? t('configuration.growOnly')
                    : undefined,
                  t('configuration.usedBy', {
                    components: [volume.owner, ...volume.mountedBy]
                      .map(component => component.label)
                      .join(', '),
                  }),
                ]
                  .filter(Boolean)
                  .join(' · '),
              ),
            )}
          </SectionCard>
        );
      case 'database': {
        const { component, roles, shared } = section;
        if (shared) {
          show(...section.fields);
          return (
            <DatabaseSection<FieldValues>
              key="section-database"
              title={component.label}
              titleId={ids('section-database')}
              rows={rows}
              fields={shared}
              modeOf={config => modeOf(config, shared.mode)}
              synchronous={plan.desired[shared.sync] === true}
            />
          );
        }
        return (
          <SectionCard
            key="section-database"
            icon={waadooIcons.database}
            title={component.label}
            titleId={ids('section-database')}
          >
            {roles.enabled && fieldRow(roles.enabled)}
            {roles.databaseMode && fieldRow(roles.databaseMode)}
            {roles.databaseSync && fieldRow(roles.databaseSync)}
            {resourceRows(component, roles, t('configuration.perInstance'))}
            {roles.databaseSize &&
              fieldRow(roles.databaseSize, t('configuration.growOnly'))}
          </SectionCard>
        );
      }
      case 'mariadb': {
        const { component, roles } = section;
        return (
          <SectionCard
            key="section-mariadb"
            icon={waadooIcons.database}
            title={component.label}
            titleId={ids('section-mariadb')}
            description={t('configuration.mariadbNote')}
          >
            {roles.enabled && fieldRow(roles.enabled)}
            {resourceRows(component, roles, t('configuration.perInstance'))}
            {roles.databaseSize &&
              fieldRow(
                roles.databaseSize,
                fieldOf(roles.databaseSize).growOnly
                  ? t('configuration.growOnly')
                  : undefined,
              )}
          </SectionCard>
        );
      }
      case 'cache': {
        // A Valkey component, titled by its label (« Cache Valkey »).
        const { component, roles } = section;
        const key = `section-cache-${component.id}`;
        return (
          <SectionCard
            key={key}
            icon={waadooIcons.memory}
            title={component.label}
            titleId={ids(key)}
          >
            {roles.enabled && fieldRow(roles.enabled)}
            {resourceRows(component, roles, t('configuration.perPod'))}
          </SectionCard>
        );
      }
      default:
        return (
          <SectionCard
            key="section-others"
            icon={waadooIcons.configuration}
            title={t('configuration.othersSection')}
            titleId={ids('section-others')}
          >
            {section.fields.map(id => fieldRow(id))}
          </SectionCard>
        );
    }
  };

  const profile = profileOf(descriptor, draft, hidden) ?? CUSTOM_PROFILE;
  const reservation = (values: FieldValues) => {
    const { cpu, memory } = sizedReservation(descriptor, values);
    return `${formatCpu(cpu)} · ${formatMemory(memory)}`;
  };
  const profileOptions: ProfileCardOption<string>[] = [
    ...descriptor.profiles.map(item => ({
      id: item.id,
      label: item.label,
      // Over the draft: a profile that leaves a sized field out keeps it.
      figures: reservation({ ...draft, ...item.values }),
    })),
    {
      id: CUSTOM_PROFILE,
      label: CUSTOM_INFO.label,
      figures: reservation(draft),
    },
  ];

  const before = peakReservation(descriptor, current, grant);
  const after = peakReservation(descriptor, plan.desired, grant);
  const canApply = applying.canApply({
    editable,
    changes: plan.changes.length,
    problems: plan.problems.length,
  });
  const lockText =
    answer.lock && answer.lock !== 'unreadable'
      ? t(LOCK_KEYS[answer.lock])
      : undefined;
  const differences = descriptor.configurableFields.filter(
    field => !hidden.has(field.id) && differs(field.id),
  ).length;

  // Built before the panel: the panel says the problems no row shows.
  const main = (
    <div className="waadoo-config__main">
      {lockText && (
        <Alert
          status="info"
          icon
          title={lockText}
          className="waadoo-config__banner"
        />
      )}
      {sleeping && (
        <Alert
          status="info"
          icon
          title={t('configuration.sleeping', { branch })}
          className="waadoo-config__banner"
        />
      )}
      {descriptor.profiles.length > 0 && (
        <SectionCard
          icon={waadooIcons.resources}
          title={t('configuration.capacityTitle')}
          description={t('configuration.capacityDescriptionGeneric', {
            app: descriptor.title,
          })}
        >
          <ProfileCards
            accessibleLabel={t('configuration.profile')}
            value={profile}
            options={profileOptions}
            onChange={id => {
              if (id !== CUSTOM_PROFILE) {
                setDraft(previous =>
                  applyProfile(descriptor, {
                    draft: previous,
                    current,
                    profileId: id,
                    locked: frozen,
                  }),
                );
              }
            }}
            isDisabled={!editable || pending}
            compact
          />
        </SectionCard>
      )}
      {layout.map(sectionOf)}
    </div>
  );
  const unplacedProblems = plan.problems
    .filter(problem => problem.field === undefined || !shown.has(problem.field))
    .map(problemText);

  const aside = (
    <ConfigurationSidePanel
      branch={branch}
      answer={answer}
      compare={{
        references: compare.references,
        referenceEnvironment,
        referenceLabel,
        select: compare.select,
        read: reference !== undefined,
        differences,
      }}
      choice={choice}
      changes={plan.changes}
      fieldLabel={id => fieldOf(id).label}
      valueText={valueText}
      problems={unplacedProblems}
      peak={{ before, after }}
      apply={applying}
      canApply={canApply}
      onApply={() => void applying.apply(plan.asked)}
      onCancel={() => setDraft(current)}
    />
  );

  return (
    <>
      <WorkbenchMain>{main}</WorkbenchMain>
      <WorkbenchAside>{aside}</WorkbenchAside>
    </>
  );
}

/**
 * The PostgreSQL mode `config` holds at `id`, a `databaseMode` field: the
 * backend reads it as one of the modes or not at all (`readConfiguration`
 * of the engine: no configuration then), the draft only takes the modes
 * offered.
 */
function modeOf(config: FieldValues, id: string): DatabaseMode {
  const value = config[id];
  if (!isDatabaseMode(value)) {
    throw new Error(`${id} : mode PostgreSQL illisible ${String(value)}`);
  }
  return value;
}
